import { useMutation, useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { ErrorMessage, Loading, Page } from "../components/common";
import { Button } from "../components/ui/button";

/** The server only registers these; anything else (javascript:, data:) would run on this origin. */
function safeRedirect(raw: string): URL | null {
  try {
    const u = new URL(raw);
    if (u.protocol === "https:") return u;
    if (u.protocol === "http:" && ["localhost", "127.0.0.1", "[::1]"].includes(u.hostname)) return u;
  } catch {
    // not an absolute URL
  }
  return null;
}

/** Consent screen for the hosted MCP OAuth flow. */
export function OAuthConsent() {
  const [params] = useSearchParams();
  const req = params.get("req") ?? "";
  const info = useQuery({
    queryKey: ["oauth", req],
    enabled: req !== "",
    queryFn: () => api.get<{ client_name: string; scope: string; redirect_uri: string }>(`/oauth/requests/${encodeURIComponent(req)}`),
  });
  const approve = useMutation({
    mutationFn: () => api.send<{ redirect: string }>("POST", "/oauth/approve", { req }),
    onSuccess: (r) => {
      const u = safeRedirect(r.redirect);
      if (u) window.location.assign(u.href);
    },
  });
  const target = info.data ? safeRedirect(info.data.redirect_uri) : null;
  return (
    <Page>
      <h1 className="mb-2 text-2xl">Allow this app</h1>
      {req === "" ? <p>Missing consent request.</p> : null}
      {info.isLoading ? <Loading /> : null}
      {info.error ? <ErrorMessage error={info.error} /> : null}
      {info.data ? (
        <div className="max-w-lg space-y-3">
          <p>
            <strong>{info.data.client_name}</strong> (unverified) wants an agent token with your read and write access (never admin), for 8 hours.
          </p>
          {target ? (
            <p>
              The token is sent to <strong>{target.host}</strong>. Approve only if you started this from that app.
            </p>
          ) : (
            <p>This request redirects to an address Bee does not allow, so it cannot be approved.</p>
          )}
          <Button variant="primary" disabled={approve.isPending || !target} onClick={() => approve.mutate()}>
            Approve
          </Button>
          {approve.error ? <ErrorMessage error={approve.error} /> : null}
        </div>
      ) : null}
    </Page>
  );
}
